import { useApi } from '../hooks/useApi';
import { API_CONFIG } from '../config/api';
import { trackEvent } from '../utils/analytics';

// Company Service for managing company-related API calls
class CompanyService {
  constructor() {
    this.apiHook = null;
  }

  // Initialize with API hook (needs to be called from React component)
  initialize(apiHook) {
    this.apiHook = apiHook;
    this.logInfo('CompanyService initialized');
  }

  // Logging utility
  logInfo(message, data = null) {
    console.log(`[CompanyService] ${message}`, data || '');
  }

  logError(message, error = null) {
    console.error(`[CompanyService] ERROR: ${message}`, error || '');
  }

  // Ensure API hook is initialized
  ensureInitialized() {
    if (!this.apiHook) {
      throw new Error('CompanyService not initialized. Call initialize() first.');
    }
  }

  /**
   * Create a new company
   * @param {Object} companyData - Company data object
   * @returns {Promise<Object>} Created company data
   */
  async createCompany(companyData) {
    this.ensureInitialized();
    this.logInfo('Creating company', companyData);

    try {
      const response = await this.apiHook.post(API_CONFIG.ENDPOINTS.COMPANY.CREATE, companyData);
      this.logInfo('Company created successfully', response);
      trackEvent('company_create');
      return response;
    } catch (error) {
      this.logError('Failed to create company', error);
      throw error;
    }
  }

  /**
   * Get list of companies for current user
   * @returns {Promise<Array>} List of companies
   */
  async listCompanies() {
    this.ensureInitialized();
    this.logInfo('Fetching companies list');

    try {
      const response = await this.apiHook.get(API_CONFIG.ENDPOINTS.COMPANY.LIST);
      this.logInfo('Companies list fetched successfully', response);
      return response.companies || [];
    } catch (error) {
      this.logError('Failed to fetch companies list', error);
      throw error;
    }
  }

  /**
   * Get company details by ID
   * @param {string} companyId - Company ID
   * @returns {Promise<Object>} Company details
   */
  async getCompany(companyId) {
    this.ensureInitialized();
    this.logInfo('Fetching company details', { companyId });

    try {
      const response = await this.apiHook.get(API_CONFIG.ENDPOINTS.COMPANY.GET(companyId));
      this.logInfo('Company details fetched successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to fetch company details', error);
      throw error;
    }
  }

  /**
   * Update company information
   * @param {string} companyId - Company ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated company data
   */
  async updateCompany(companyId, updateData) {
    this.ensureInitialized();
    this.logInfo('Updating company', { companyId, updateData });

    try {
      const response = await this.apiHook.put(API_CONFIG.ENDPOINTS.COMPANY.UPDATE(companyId), updateData);
      this.logInfo('Company updated successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to update company', error);
      throw error;
    }
  }

  /**
   * Update company with file uploads (comprehensive profile update)
   * @param {string} companyId - Company ID
   * @param {Object} textData - Text field data to update
   * @param {Object} fileData - File data objects with metadata
   * @returns {Promise<Object>} Updated company data
   */
  async updateCompanyWithFiles(companyId, textData, fileData = {}) {
    this.ensureInitialized();
    this.logInfo('Updating company with files', { companyId, textData, fileData });

    const uploadCounts = {
      logo: Array.isArray(fileData.logo) ? fileData.logo.length : 0,
      brand_images: Array.isArray(fileData.brand_images) ? fileData.brand_images.length : 0,
      documents: Array.isArray(fileData.documents) ? fileData.documents.length : 0
    };

    try {
      const formData = new FormData();

      // Add text fields
      Object.entries(textData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // Serialize objects and arrays to JSON string
          const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
          formData.append(key, serializedValue);
        }
      });

      // Add logo files with metadata
      if (fileData.logo && fileData.logo.length > 0) {
        fileData.logo.forEach((logoData, index) => {
          // Validate that we have a proper File object
          if (!(logoData.file instanceof File)) {
            throw new Error('Logo must be a valid File object');
          }

          formData.append('logo', logoData.file);
          if (logoData.description) {
            formData.append('logo_description', logoData.description);
          }
          if (logoData.tags && logoData.tags.length > 0) {
            formData.append('logo_tags', logoData.tags.join(','));
          }
          if (logoData.usage_context && logoData.usage_context.length > 0) {
            formData.append('logo_usage_context', logoData.usage_context.join(','));
          }
        });
      }

      // Add brand image files
      if (fileData.brand_images && fileData.brand_images.length > 0) {
        fileData.brand_images.forEach((imageData, index) => {
          formData.append('brand_images', imageData.file);
          if (imageData.description) {
            formData.append('brand_image_description', imageData.description);
          }
          if (imageData.tags && imageData.tags.length > 0) {
            formData.append('brand_image_tags', imageData.tags.join(','));
          }
          if (imageData.usage_context && imageData.usage_context.length > 0) {
            formData.append('brand_image_usage_context', imageData.usage_context.join(','));
          }
        });
      }

      // Add document files
      if (fileData.documents && fileData.documents.length > 0) {
        fileData.documents.forEach((docData, index) => {
          formData.append('documents', docData.file);
          if (docData.description) {
            formData.append('document_description', docData.description);
          }
          if (docData.tags && docData.tags.length > 0) {
            formData.append('document_tags', docData.tags.join(','));
          }
          if (docData.usage_context && docData.usage_context.length > 0) {
            formData.append('document_usage_context', docData.usage_context.join(','));
          }
        });
      }

      // Use upload method for multipart/form-data
      const response = await this.apiHook.upload(API_CONFIG.ENDPOINTS.COMPANY.UPDATE(companyId), formData);
      this.logInfo('Company updated successfully with files', response);
      const totalUploads = uploadCounts.logo + uploadCounts.brand_images + uploadCounts.documents;
      if (totalUploads > 0) {
        trackEvent('media_upload', {
          entity: 'company',
          file_count: totalUploads,
          logo_count: uploadCounts.logo,
          brand_image_count: uploadCounts.brand_images,
          document_count: uploadCounts.documents
        });
      }
      return response;
    } catch (error) {
      this.logError('Failed to update company with files', error);
      throw error;
    }
  }

  /**
   * Upload documents to company
   * @param {string} companyId - Company ID
   * @param {File} file - File to upload
   * @param {string} fileType - Type description of the file
   * @returns {Promise<Object>} Upload response
   */
  async uploadDocument(companyId, file, fileType = '') {
    this.ensureInitialized();
    this.logInfo('Uploading document to company', { companyId, fileName: file.name, fileType });

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (fileType) {
        formData.append('file_type', fileType);
      }

      // Use multipart upload
      const response = await this.apiHook.upload(API_CONFIG.ENDPOINTS.COMPANY.UPLOAD(companyId), formData);
      this.logInfo('Document uploaded successfully', response);
      trackEvent('media_upload', {
        entity: 'company',
        file_count: 1,
        file_type: fileType || 'document'
      });
      return response;
    } catch (error) {
      this.logError('Failed to upload document', error);
      throw error;
    }
  }

  /**
   * Trigger AI analysis for company
   * @param {string} companyId - Company ID
   * @param {Object} analysisOptions - Analysis options
   * @returns {Promise<Object>} Analysis response
   */
  async analyzeCompany(companyId, analysisOptions = {}) {
    this.ensureInitialized();
    this.logInfo('Starting AI analysis for company', { companyId, analysisOptions });

    try {
      const defaultOptions = {
        include_website: true,
        analysis_type: 'full'
      };
      
      const options = { ...defaultOptions, ...analysisOptions };
      const response = await this.apiHook.post(API_CONFIG.ENDPOINTS.COMPANY.ANALYZE(companyId), options);
      this.logInfo('AI analysis completed successfully', response);
      trackEvent('company_analyze', {
        analysis_type: options.analysis_type,
        include_website: options.include_website
      });
      return response;
    } catch (error) {
      this.logError('Failed to perform AI analysis', error);
      throw error;
    }
  }

  /**
   * Delete company
   * @param {string} companyId - Company ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteCompany(companyId) {
    this.ensureInitialized();
    this.logInfo('Deleting company', { companyId });

    try {
      const response = await this.apiHook.delete(API_CONFIG.ENDPOINTS.COMPANY.DELETE(companyId));
      this.logInfo('Company deleted successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to delete company', error);
      throw error;
    }
  }

  /**
   * Upload multiple files (batch upload)
   * @param {string} companyId - Company ID
   * @param {Array<File>} files - Files to upload
   * @param {string} fileType - Type description of the files
   * @returns {Promise<Array>} Array of upload responses
   */
  async uploadMultipleDocuments(companyId, files, fileType = '') {
    this.ensureInitialized();
    this.logInfo('Uploading multiple documents', { companyId, fileCount: files.length, fileType });

    try {
      const uploadPromises = files.map(file => 
        this.uploadDocument(companyId, file, fileType)
      );

      const responses = await Promise.allSettled(uploadPromises);
      
      const successful = responses.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = responses.filter(r => r.status === 'rejected').map(r => r.reason);

      this.logInfo('Batch upload completed', { 
        successful: successful.length, 
        failed: failed.length 
      });

      if (failed.length > 0) {
        this.logError('Some uploads failed', failed);
      }

      return {
        successful,
        failed,
        totalCount: files.length,
        successCount: successful.length,
        failureCount: failed.length
      };
    } catch (error) {
      this.logError('Failed to upload multiple documents', error);
      throw error;
    }
  }

  /**
   * Generate (or regenerate) company DNA using the AI endpoint.
   * @param {string} companyId - Company ID to analyze
   * @param {Object} analysisOptions - Analysis options
   * @returns {Promise<Object>} Analysis response with updated company data
   */
  async analyzeCompanyMaterials(companyId, analysisOptions = {}) {
    this.ensureInitialized();
    this.logInfo('Starting AI analysis for company materials', { companyId, analysisOptions });

    try {
      const defaultOptions = {
        analysis_type: 'standard',
        include_website: true,
        include_documents: true,
        include_images: true
      };
      
      const options = { ...defaultOptions, ...analysisOptions };
      const response = await this.apiHook.post(API_CONFIG.ENDPOINTS.AI.COMPANY_ANALYZE(companyId), options);
      this.logInfo('AI company DNA generation completed successfully', response);
      trackEvent('company_dna_generate', {
        analysis_type: options.analysis_type,
        include_website: options.include_website,
        include_documents: options.include_documents,
        include_images: options.include_images
      });
      return response;
    } catch (error) {
      this.logError('Failed to perform AI company materials analysis', error);
      throw error;
    }
  }

  /**
   * Get analysis progress update for a request
   * @param {string} companyId - Company ID
   * @param {string} requestId - Analysis request ID 
   * @returns {Promise<Object>} Progress update with stage, message, and percentage
   */
  async getAnalysisProgress(companyId, requestId) {
    throw new Error('Analysis progress is not supported: company DNA generation is synchronous. Call analyzeCompanyMaterials() and use response.dna.');
  }

  /**
   * Get final analysis result and update company data
   * @param {string} companyId - Company ID that was analyzed
   * @param {string} requestId - Analysis request ID
   * @returns {Promise<Object>} Analysis result with updated company data
   */
  async getAnalysisResult(companyId, requestId) {
    throw new Error('Analysis result is not supported: company DNA generation is synchronous. Call analyzeCompanyMaterials() and use response.dna.');
  }

  /**
   * Analyze company from website URL (legacy method for backward compatibility)
   * @param {string} websiteUrl - Website URL to analyze
   * @param {Object} analysisOptions - Additional analysis options
   * @returns {Promise<Object>} Analysis response with suggested company data
   */
  async analyzeFromWebsite(websiteUrl, analysisOptions = {}) {
    this.ensureInitialized();
    this.logInfo('Analyzing company from website', { websiteUrl, analysisOptions });

    try {
      // Create a temporary company with unique name to avoid duplicates
      const timestamp = new Date().getTime();
      const tempCompanyData = {
        name: `Temporary Company for Analysis ${timestamp}`,
        website: websiteUrl,
        description: 'Temporary company created for website analysis'
      };

      // First create the company
      const company = await this.createCompany(tempCompanyData);
      
      // Then run analysis
      const analysisResult = await this.analyzeCompany(company.id, {
        include_website: true,
        analysis_type: 'website_only',
        ...analysisOptions
      });

      this.logInfo('Website analysis completed', analysisResult);
      return {
        company,
        analysis: analysisResult
      };
    } catch (error) {
      this.logError('Failed to analyze website', error);
      throw error;
    }
  }
}

// Create singleton instance
const companyService = new CompanyService();

export default companyService; 
